import { useCallback } from 'react';
import { APPOINTMENTS_MODULE, CONTESTS_MODULE, useHasSpecialModule, WISHLIST_TEMPLATES_MODULE } from '~/lib/auth/use-has-special-module';
import { isFeatureAvailable, type MenuFeature } from '~/lib/menu';
import { useReturnsEnabled } from './use-returns';

/**
 * Resolves the `requiredFeature` gate that `~/lib/menu` declares, for the sidebar and the command palette
 * alike — the two surfaces must agree, so they read the same hook rather than each deciding.
 *
 * A feature is a **per-site module switch**, which is a different question from `requiredScope`: the scope
 * asks whether this administrator may see the page, the feature asks whether the page exists in this shop at
 * all. Admin-v1 hides the Returns entry the same way (`helpers/useReturnsEnabled`), and BW-7550's DoD calls
 * that the correct behaviour — an unseeded repository shows no Returns section.
 *
 * Unknown counts as **available**: the switch is only "off" once the server has positively said so. Hiding
 * navigation while the query is in flight makes the rail jump on every load, and hiding it because a request
 * failed removes the page that would have explained the failure. `useReturnsEnabled` already draws that line
 * — only a 404 resolves to `false`.
 *
 * `appointments`, `contests` (BW-8453) and `wishlistTemplates` (BW-8477) are answered synchronously from the session's special modules (login
 * `spmods`), so they are never "unknown": an off-by-default module must not flash into the menu on first paint.
 */
export function useMenuFeatures(): (feature: MenuFeature) => boolean {
	const { data: returnsEnabled } = useReturnsEnabled();

	// A map rather than a switch with a `default`, so adding a member to `MenuFeature` becomes a compile error
	// here instead of silently defaulting the new feature to "always visible".
	const returns = isFeatureAvailable(returnsEnabled);
	const appointments = useHasSpecialModule(APPOINTMENTS_MODULE);
	const contests = useHasSpecialModule(CONTESTS_MODULE);
	const wishlistTemplates = useHasSpecialModule(WISHLIST_TEMPLATES_MODULE);

	return useCallback(
		(feature: MenuFeature) => {
			const available: Record<MenuFeature, boolean> = { returns, appointments, contests, wishlistTemplates };

			return available[feature];
		},
		[returns, appointments, contests, wishlistTemplates]
	);
}
